import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FOCUS_LABELS = {
  arts_martiaux: "Arts martiaux",
  force_conditionnement: "Force & conditionnement",
  perte_poids: "Perte de poids",
  nutrition: "Nutrition",
  hybride: "Hybride",
};

const LOCATION_LABELS = {
  dojo: "🥋 Dojo",
  visio: "💻 Visio",
  exterieur: "🌳 Extérieur",
};

function StatChip({ label, value, accent }) {
  return (
    <div className={`rounded-xl border ${accent ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/50"} p-4`}>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-amber-300" : "text-zinc-100"}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function CoachingClientPortal() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const [nutritionOpen, setNutritionOpen] = useState(false);
  const [metricOpen, setMetricOpen] = useState(false);
  const [nutForm, setNutForm] = useState({ adherence_1_10: 7, meals_notes: "", water_litres: "" });
  const [metForm, setMetForm] = useState({ weight_kg: "", waist_cm: "", sleep_hours: "", energy_1_10: "", stress_1_10: "", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/coaching/portal/${slug}`);
      setData(r.data);
      setErr(false);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [slug]);

  const submitNutrition = async () => {
    try {
      const payload = {
        adherence_1_10: Number(nutForm.adherence_1_10),
        meals_notes: nutForm.meals_notes || "",
        water_litres: nutForm.water_litres ? Number(nutForm.water_litres) : null,
      };
      const r = await axios.post(`${API}/coaching/portal/${slug}/log-nutrition`, payload);
      toast.success(r.data.message);
      setNutritionOpen(false);
      setNutForm({ adherence_1_10: 7, meals_notes: "", water_litres: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    }
  };

  const submitMetric = async () => {
    try {
      const payload = {};
      for (const [k, v] of Object.entries(metForm)) {
        if (v !== "" && v !== null) payload[k] = ["note"].includes(k) ? v : Number(v);
      }
      const r = await axios.post(`${API}/coaching/portal/${slug}/log-metric`, payload);
      toast.success(r.data.message);
      setMetricOpen(false);
      setMetForm({ weight_kg: "", waist_cm: "", sleep_hours: "", energy_1_10: "", stress_1_10: "", note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center">
        <div className="text-sm">Chargement…</div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-3">KAIZEN COACHING PRIVÉ</div>
          <h1 className="text-3xl font-bold text-zinc-100 mb-4">Portail introuvable</h1>
          <p className="text-zinc-500 text-sm">Le lien d'accès n'est plus valide ou a été révoqué. Contacte ton coach pour en obtenir un nouveau.</p>
        </div>
      </div>
    );
  }

  const c = data.client;
  const m = data.latest_metric;
  const nb = data.next_booking;
  const ns = data.next_session;
  const adherence = data.avg_adherence_7d;

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="coaching-client-portal">
      {/* Hero */}
      <div className="border-b border-zinc-900 bg-gradient-to-br from-zinc-950 via-black to-zinc-950">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-3">KAIZEN · TON STUDIO PRIVÉ</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-zinc-50" data-testid="portal-client-name">
            Bonjour {c.full_name?.split(" ")[0]}.
          </h1>
          <p className="text-zinc-500 mt-3 text-lg italic" data-testid="portal-kaizen-message">
            « {data.kaizen_message} »
          </p>
          <div className="flex gap-2 mt-6 flex-wrap">
            {c.coaching_focus && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                {FOCUS_LABELS[c.coaching_focus] || c.coaching_focus}
              </Badge>
            )}
            {c.package && <Badge variant="outline" className="border-zinc-700 text-zinc-300">{c.package}</Badge>}
            {c.sessions_remaining != null && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid="portal-sessions-remaining">
                {c.sessions_remaining} séances restantes
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        {/* Objectif */}
        {c.primary_goal && (
          <section data-testid="portal-goal">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">OBJECTIF PRINCIPAL</div>
            <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
              <div className="text-xl text-zinc-100">{c.primary_goal}</div>
              {c.target_date && <div className="text-xs text-zinc-500 mt-2">Échéance : {c.target_date}</div>}
              {c.why_personal && (
                <div className="mt-4 pt-4 border-t border-zinc-900">
                  <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-1">POURQUOI C'EST IMPORTANT POUR TOI</div>
                  <div className="text-sm text-zinc-300 italic">{c.why_personal}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portal-stats">
          <StatChip label="Poids" value={m?.weight_kg ? `${m.weight_kg} kg` : null} />
          <StatChip label="Tour de taille" value={m?.waist_cm ? `${m.waist_cm} cm` : null} />
          <StatChip label="Sommeil moyen" value={m?.sleep_hours ? `${m.sleep_hours} h` : null} />
          <StatChip label="Adhérence 7j" value={adherence ? `${adherence}/10` : null} accent />
        </section>

        {/* Prochaine action */}
        <section className="grid md:grid-cols-2 gap-4" data-testid="portal-next">
          <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">PROCHAIN RENDEZ-VOUS</div>
            {nb ? (
              <div data-testid="portal-next-booking">
                <div className="text-2xl font-bold text-zinc-50">
                  {new Date(nb.scheduled_at).toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" })}
                </div>
                <div className="text-sm text-zinc-400 mt-1">
                  {new Date(nb.scheduled_at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
                  {nb.duration_min && ` · ${nb.duration_min} min`}
                  {nb.location && ` · ${LOCATION_LABELS[nb.location] || nb.location}`}
                </div>
                {nb.notes && <div className="text-xs text-zinc-500 mt-3 italic">{nb.notes}</div>}
              </div>
            ) : (
              <div className="text-sm text-zinc-500">Aucun rendez-vous prévu. Contacte ton coach.</div>
            )}
          </div>

          <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">PROCHAINE SÉANCE À FAIRE</div>
            {ns ? (
              <div data-testid="portal-next-session">
                <div className="text-2xl font-bold text-zinc-50">{ns.title || "Séance d'entraînement"}</div>
                {ns.program_label && <div className="text-sm text-amber-300 mt-1">{ns.program_label}</div>}
                {ns.scheduled_at && (
                  <div className="text-xs text-zinc-500 mt-2">
                    {new Date(ns.scheduled_at).toLocaleDateString("fr-CA", { day: "numeric", month: "long" })}
                  </div>
                )}
                {ns.coach_notes && <div className="text-xs text-zinc-400 mt-3 border-l-2 border-amber-500/40 pl-3 italic">{ns.coach_notes}</div>}
              </div>
            ) : (
              <div className="text-sm text-zinc-500">Aucune séance assignée. Repos mérité 🧘</div>
            )}
          </div>
        </section>

        {/* Actions self-service */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">LOG SELF-SERVICE</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Button
              onClick={() => setNutritionOpen(true)}
              className="h-auto py-5 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl"
              data-testid="portal-log-nutrition-btn"
            >
              🥗 Logger ma journée nutrition
            </Button>
            <Button
              onClick={() => setMetricOpen(true)}
              variant="outline"
              className="h-auto py-5 border-zinc-700 hover:bg-zinc-900 rounded-xl"
              data-testid="portal-log-metric-btn"
            >
              📏 Logger une mesure
            </Button>
          </div>
        </section>

        {/* Notes du coach */}
        {data.coach_notes?.length > 0 && (
          <section data-testid="portal-coach-notes">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">MESSAGES DE TON COACH</div>
            <div className="space-y-3">
              {data.coach_notes.map((n) => (
                <div key={n.id} className="border-l-2 border-amber-500/40 pl-4 py-2">
                  <div className="text-xs text-zinc-500">
                    {new Date(n.created_at).toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                  <div className="text-sm text-zinc-200 mt-1 whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Historique nutrition récent */}
        {data.recent_nutrition?.length > 0 && (
          <section data-testid="portal-recent-nutrition">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">HISTORIQUE NUTRITION (7 DERNIERS JOURS)</div>
            <div className="space-y-2">
              {data.recent_nutrition.map((n) => {
                const dotColor = n.adherence_1_10 >= 7 ? "bg-emerald-400" : n.adherence_1_10 >= 4 ? "bg-amber-400" : "bg-red-400";
                return (
                  <div key={n.id} className="flex items-center gap-3 border border-zinc-900 rounded-lg p-3 bg-zinc-950/30">
                    <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
                    <div className="flex-1">
                      <div className="text-xs text-zinc-500">
                        {new Date(n.log_date).toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", month: "short" })}
                      </div>
                      {n.meals_notes && <div className="text-sm text-zinc-300 mt-0.5 line-clamp-1">{n.meals_notes}</div>}
                    </div>
                    <div className="text-amber-300 font-bold text-sm">{n.adherence_1_10}/10</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-zinc-700 pt-10 pb-6 border-t border-zinc-900">
          KAIZEN System™ · Coaching privé · Confidentiel
        </div>
      </div>

      {/* Nutrition Dialog */}
      <Dialog open={nutritionOpen} onOpenChange={setNutritionOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="nutrition-dialog">
          <DialogHeader><DialogTitle>Logger ta journée nutrition</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-400">Adhérence (1-10) <span className="text-amber-300">{nutForm.adherence_1_10}</span></Label>
              <input
                type="range" min="1" max="10" value={nutForm.adherence_1_10}
                onChange={(e) => setNutForm({ ...nutForm, adherence_1_10: e.target.value })}
                className="w-full mt-2"
                data-testid="nutrition-adherence-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Eau (litres) — optionnel</Label>
              <Input
                type="number" step="0.1" value={nutForm.water_litres}
                onChange={(e) => setNutForm({ ...nutForm, water_litres: e.target.value })}
                placeholder="2.5"
                className="bg-black border-zinc-800"
                data-testid="nutrition-water-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes repas — optionnel</Label>
              <Textarea
                value={nutForm.meals_notes}
                onChange={(e) => setNutForm({ ...nutForm, meals_notes: e.target.value })}
                placeholder="Petit déj : œufs + flocons d'avoine. Midi : poulet/riz. Soir : poisson/légumes."
                className="bg-black border-zinc-800 min-h-[80px]"
                data-testid="nutrition-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNutritionOpen(false)}>Annuler</Button>
            <Button onClick={submitNutrition} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="nutrition-submit-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metric Dialog */}
      <Dialog open={metricOpen} onOpenChange={setMetricOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="metric-dialog">
          <DialogHeader><DialogTitle>Logger une mesure</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Poids (kg)</Label>
              <Input type="number" step="0.1" value={metForm.weight_kg} onChange={(e) => setMetForm({ ...metForm, weight_kg: e.target.value })} className="bg-black border-zinc-800" data-testid="metric-weight-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Tour de taille (cm)</Label>
              <Input type="number" step="0.1" value={metForm.waist_cm} onChange={(e) => setMetForm({ ...metForm, waist_cm: e.target.value })} className="bg-black border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Sommeil (h)</Label>
              <Input type="number" step="0.1" value={metForm.sleep_hours} onChange={(e) => setMetForm({ ...metForm, sleep_hours: e.target.value })} className="bg-black border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Énergie (1-10)</Label>
              <Input type="number" min="1" max="10" value={metForm.energy_1_10} onChange={(e) => setMetForm({ ...metForm, energy_1_10: e.target.value })} className="bg-black border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Stress (1-10)</Label>
              <Input type="number" min="1" max="10" value={metForm.stress_1_10} onChange={(e) => setMetForm({ ...metForm, stress_1_10: e.target.value })} className="bg-black border-zinc-800" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-400">Note</Label>
              <Input value={metForm.note} onChange={(e) => setMetForm({ ...metForm, note: e.target.value })} className="bg-black border-zinc-800" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMetricOpen(false)}>Annuler</Button>
            <Button onClick={submitMetric} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="metric-submit-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
